import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loader,
  ScrollArea,
} from "@devhop/ui";
import { Check } from "lucide-react";
import { useBranchTables } from "../api/queries";

type SelectableTable = NonNullable<
  ReturnType<typeof useBranchTables>["data"]
>[number];

type TableSelectorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branchId: string | null;
  currentTableId: string | null;
  onSelect: (tableId: string | null, table?: SelectableTable | null) => void;
};

export function TableSelectorDialog({
  open,
  onOpenChange,
  branchId,
  currentTableId,
  onSelect,
}: TableSelectorDialogProps) {
  const { data: tables, isLoading } = useBranchTables(branchId);

  const handleSelect = (
    tableId: string | null,
    table?: SelectableTable | null,
  ) => {
    onSelect(tableId, table);
    onOpenChange(false);
  };

  // Group tables by zone
  const tablesByZone = new Map<string, typeof tables>();
  if (tables) {
    for (const table of tables) {
      const zone = table.zone || "ອື່ນໆ";
      if (!tablesByZone.has(zone)) {
        tablesByZone.set(zone, []);
      }
      tablesByZone.get(zone)?.push(table);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>ເລືອກໂຕະ</DialogTitle>
          <DialogDescription>ເລືອກໂຕະທີ່ຕ້ອງການຜູກກັບ order ນີ້</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader />
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-4">
              {/* Option to remove table */}
              <button
                type="button"
                onClick={() => handleSelect(null, null)}
                className={cn(
                  "flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors hover:bg-accent",
                  currentTableId === null && "border-primary bg-accent",
                )}
              >
                <span className="font-medium">ບໍ່ມີໂຕະ</span>
                {currentTableId === null && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </button>

              {/* Tables grouped by zone */}
              {Array.from(tablesByZone.entries()).map(([zone, zoneTables]) => (
                <div key={zone} className="space-y-2">
                  <h3 className="font-semibold text-muted-foreground text-sm">
                    {zone}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {zoneTables?.map((table) => (
                      <button
                        key={table.id}
                        type="button"
                        onClick={() => handleSelect(table.id, table)}
                        disabled={
                          table.isOccupied && table.id !== currentTableId
                        }
                        className={cn(
                          "flex items-center justify-between rounded-lg border p-3 text-left transition-colors",
                          table.id === currentTableId
                            ? "border-primary bg-accent"
                            : "hover:bg-accent",
                          table.isOccupied &&
                            table.id !== currentTableId &&
                            "cursor-not-allowed opacity-50",
                        )}
                      >
                        <div className="flex-1">
                          <p className="font-medium">{table.name}</p>
                          {table.capacity && (
                            <p className="text-muted-foreground text-xs">
                              {table.capacity} ທີ່ນັ່ງ
                            </p>
                          )}
                        </div>
                        {table.isOccupied && table.id !== currentTableId && (
                          <span className="text-muted-foreground text-xs">
                            ມີຄົນ
                          </span>
                        )}
                        {table.id === currentTableId && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ຍົກເລີກ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
